/**
 * IncidentReport — dialog for reporting a crisis incident.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flame, Droplets, Zap, Car, Shield, Heart, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = [
  { value: 'fire', label: 'Fire', icon: Flame, color: '#ef4444' },
  { value: 'flood', label: 'Flood', icon: Droplets, color: '#3b82f6' },
  { value: 'power_outage', label: 'Power Out', icon: Zap, color: '#eab308' },
  { value: 'road_accident', label: 'Accident', icon: Car, color: '#f97316' },
  { value: 'violence', label: 'Violence', icon: Shield, color: '#8b5cf6' },
  { value: 'medical', label: 'Medical', icon: Heart, color: '#ec4899' },
  { value: 'other', label: 'Other', icon: AlertTriangle, color: '#6b7280' },
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: '#22c55e' },
  { value: 'medium', label: 'Medium', color: '#eab308' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'critical', label: 'Critical', color: '#ef4444' },
];

export const IncidentReport = ({ open, onClose, userLocation, mapCenter }) => {
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!type) { toast.error('Select an incident type'); return; }
    const loc = userLocation || mapCenter;
    if (!loc) { toast.error('Location required — enable GPS or set a postcode'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/crisis/incidents`, {
        type, severity, description,
        lat: loc.lat, lng: loc.lng,
      });
      toast.success('Incident reported');
      setType(''); setDescription(''); setSeverity('medium');
      onClose(true);
    } catch (err) {
      const msg = err?.response?.status === 401 ? 'Please log in to report incidents' : 'Failed to report incident';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Report Incident</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type grid */}
          <div>
            <p className="text-xs text-muted-foreground mb-2">What's happening?</p>
            <div className="grid grid-cols-4 gap-2">
              {INCIDENT_TYPES.map(t => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setType(t.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl text-[10px] font-medium border-2 transition-all ${
                      active ? 'text-white border-transparent' : 'text-muted-foreground border-border/40 hover:border-border'
                    }`}
                    style={active ? { backgroundColor: t.color, borderColor: t.color } : {}}
                    data-testid={`incident-type-${t.value}`}
                  >
                    <Icon className="w-5 h-5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Severity */}
          <div>
            <p className="text-xs text-muted-foreground mb-2">Severity</p>
            <div className="flex gap-1.5">
              {SEVERITY_LEVELS.map(s => (
                <button
                  key={s.value}
                  onClick={() => setSeverity(s.value)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${
                    severity === s.value ? 'text-white border-transparent' : 'text-muted-foreground border-border/40'
                  }`}
                  style={severity === s.value ? { backgroundColor: s.color, borderColor: s.color } : {}}
                  data-testid={`incident-severity-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description (optional)..."
            className="w-full bg-muted/50 border border-border/40 rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none resize-none"
            rows={2}
            data-testid="incident-description"
          />

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !type}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all"
            style={{ backgroundColor: '#ef4444' }}
            data-testid="incident-submit-btn"
          >
            {submitting ? 'Reporting...' : 'Report Incident'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
